(() => {
  "use strict";

  // Each initialized element gets a controller with a destroy() for section unload.
  const controllers = new WeakMap();

  const getApiBase = (element) => {
    const local = element?.dataset.apiBase;
    const global = document.body?.dataset.faithApi;
    return String(local || global || "").trim().replace(/\/+$/, "");
  };

  const showMessage = (target, message, isError = false) => {
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
    target.classList.toggle("error-message", isError);
    target.classList.toggle("success-message", !isError);
  };

  const hideMessage = (target) => {
    if (!target) return;
    target.hidden = true;
    target.textContent = "";
  };

  const friendlyError = (error, fallback) => {
    if (error && error.name === "AbortError") return "";
    if (error instanceof TypeError) return "We could not reach the prayer service. Please check your connection and try again.";
    return error instanceof Error && error.message ? error.message : fallback;
  };

  const readError = async (response, fallback) => {
    try {
      const data = await response.json();
      if (data && typeof data.error === "string") return data.error;
    } catch {
      /* not JSON */
    }
    if (response.status === 429) return "Many people are praying right now. Please wait a moment and try again.";
    return fallback;
  };

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  /* Menu toggle */
  const initMenu = (toggle) => {
    const onClick = () => {
      const nav = document.getElementById(toggle.getAttribute("aria-controls"));
      const expanded = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!expanded));
      nav?.classList.toggle("is-open", !expanded);
    };
    toggle.addEventListener("click", onClick);
    return { destroy: () => toggle.removeEventListener("click", onClick) };
  };

  /* Prayer library: native DOM filtering over server-rendered items */
  const initLibrary = (section) => {
    const search = section.querySelector("[data-library-search]");
    const items = Array.from(section.querySelectorAll("[data-library-item]"));
    const empty = section.querySelector("[data-library-empty]");
    const count = section.querySelector("[data-library-count]");
    if (!search) return { destroy() {} };
    const apply = () => {
      const terms = search.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
      let shown = 0;
      items.forEach((item) => {
        const text = item.dataset.searchText || item.textContent.toLowerCase();
        const match = terms.every((term) => text.includes(term));
        item.hidden = !match;
        if (match) shown += 1;
      });
      if (empty) empty.hidden = shown > 0;
      if (count) count.textContent = terms.length ? `${shown} of ${items.length} prayers shown` : "";
    };
    search.addEventListener("input", apply);
    apply();
    return { destroy: () => search.removeEventListener("input", apply) };
  };

  /* Personalized prayer generator */
  // Only remove explicit narration cues, not ordinary phrases such as
  // "this period of waiting" or "help me pause and breathe".
  const cleanNarration = (value) => String(value || "")
    .replace(/<break\b[^>]*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/[\[({]\s*(?:(?:short|long|gentle|brief)\s+)?pause(?:\s+(?:for\s+)?\d+(?:\.\d+)?\s*(?:s|seconds?))?\s*[.!]?\s*[\])}]/gi, "\n")
    .replace(/[\[({]\s*(?:period|full stop)\s*[.!]?\s*[\])}]/gi, ".")
    .replace(/^\s*(?:(?:short|long|gentle|brief)\s+)?(?:pause|period|full stop)\s*[.!]?\s*$/gim, "\n")
    .replace(/(^|[.!?\n]\s*)(?:pause|period|full stop)\s*(?:[.!?]\s*|$)/gi, "$1\n")
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/\*\*|__/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const initGenerator = (section) => {
    const form = section.querySelector("form");
    const output = section.querySelector("[data-prayer-result]");
    const message = section.querySelector("[data-prayer-message]");
    const submit = form?.querySelector("[type=submit]");
    const submitLabel = submit?.textContent || "Create my prayer";
    const audio = output?.querySelector("[data-prayer-audio]");
    const playButton = output?.querySelector("[data-audio-play]");
    const replayButton = output?.querySelector("[data-audio-replay]");
    const downloadButton = output?.querySelector("[data-audio-download]");
    const textButton = output?.querySelector("[data-prayer-download-text]");
    const copyButton = output?.querySelector("[data-prayer-copy]");
    const speed = output?.querySelector("[data-audio-speed]");
    const audioMessage = output?.querySelector("[data-audio-message]");
    const cleanups = [];
    const on = (el, type, fn) => {
      if (!el) return;
      el.addEventListener(type, fn);
      cleanups.push(() => el.removeEventListener(type, fn));
    };
    const applyLinkedTopic = () => {
      const topic = new URL(window.location.href).searchParams.get("prayer_topic");
      const field = form?.querySelector('[name="category"]');
      if (topic && field && Array.from(field.querySelectorAll("option")).some((option) => (option.value || option.textContent) === topic)) {
        field.value = topic;
      }
    };
    applyLinkedTopic();
    on(window, "popstate", applyLinkedTopic);

    let prayer = null; // current prayer result
    let generation = 0; // increments for every new prayer; guards stale audio
    let audioUrl = "";
    let audioBlob = null;
    let audioPromise = null;
    let prayerController = null;
    let audioController = null;

    const currentSpeed = () => Number(speed?.value) || 0.9;

    const resetAudio = () => {
      audioController?.abort();
      audioController = null;
      audioPromise = null;
      if (audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
        audio.hidden = true;
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      audioUrl = "";
      audioBlob = null;
      if (playButton) {
        playButton.textContent = "Listen";
        playButton.disabled = false;
        playButton.setAttribute("aria-pressed", "false");
      }
      if (downloadButton) downloadButton.disabled = false;
      hideMessage(audioMessage);
    };

    const prayerAsText = () => {
      if (!prayer) return "";
      return [
        prayer.title || "A prayer for you",
        "",
        `${prayer.scriptureReference || ""}: ${prayer.scriptureText || ""}`,
        "",
        "A thought to carry",
        prayer.reflection || "",
        "",
        "Your prayer",
        prayer.prayer || "",
        "",
        "One small next step",
        prayer.nextStep || "",
      ].map(cleanNarration).join("\n");
    };

    // Fetch audio for the current prayer once; result discarded if a new prayer arrives.
    const ensureAudio = () => {
      if (audioBlob) return Promise.resolve(audioBlob);
      if (audioPromise) return audioPromise;
      const base = getApiBase(section);
      if (!base || !prayer?.prayer) return Promise.reject(new Error("Audio is not available right now."));
      const myGeneration = generation;
      audioController = new AbortController();
      // Narrate the complete result in its visible order, not just the prayer.
      // Replay and audio download reuse this same complete, cleaned recording.
      const body = { text: cleanNarration(prayerAsText()) };
      const voiceId = (section.dataset.voiceId || "").trim();
      if (voiceId) body.voiceId = voiceId;
      audioPromise = (async () => {
        const response = await fetch(`${base}/api/prayers/audio`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "audio/mpeg" },
          credentials: "omit",
          body: JSON.stringify(body),
          signal: audioController.signal,
        });
        if (!response.ok) throw new Error(await readError(response, "Audio could not be prepared right now."));
        const blob = await response.blob();
        if (myGeneration !== generation) {
          const stale = new Error("stale");
          stale.name = "AbortError";
          throw stale;
        }
        audioBlob = blob;
        audioUrl = URL.createObjectURL(blob);
        audio.src = audioUrl;
        audio.hidden = false;
        audio.playbackRate = currentSpeed();
        return blob;
      })();
      audioPromise.catch(() => {
        if (myGeneration === generation) audioPromise = null;
      });
      return audioPromise;
    };

    const play = async () => {
      try {
        audio.playbackRate = currentSpeed();
        await audio.play();
      } catch {
        showMessage(audioMessage, "Audio could not be played in this browser. You can still download it.", true);
      }
    };

    on(form, "submit", async (event) => {
      event.preventDefault();
      const base = getApiBase(section);
      if (!base) {
        showMessage(message, "Personalized prayers are not available on this site right now. The prayer library and daily Scripture are always open to you.", true);
        return;
      }
      const data = new FormData(form);
      const body = {
        category: String(data.get("category") || ""),
        duration: Number(data.get("duration") || 3),
        situation: String(data.get("situation") || ""),
      };
      prayerController?.abort();
      prayerController = new AbortController();
      generation += 1;
      const myGeneration = generation;
      prayer = null;
      resetAudio();
      submit.disabled = true;
      submit.textContent = "Preparing your prayer...";
      hideMessage(message);
      output.hidden = true;
      try {
        const response = await fetch(`${base}/api/prayers/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "omit",
          body: JSON.stringify(body),
          signal: prayerController.signal,
        });
        if (!response.ok) throw new Error(await readError(response, "The prayer could not be created right now. Please try again."));
        const result = await response.json();
        if (myGeneration !== generation) return;
        prayer = result;
        output.querySelector("[data-prayer-title]").textContent = result.title || "A prayer for you";
        output.querySelector("[data-prayer-verse]").textContent = `${result.scriptureReference || ""}: ${result.scriptureText || ""}`;
        output.querySelector("[data-prayer-reflection]").textContent = result.reflection || "";
        output.querySelector("[data-prayer-text]").textContent = cleanNarration(result.prayer);
        output.querySelector("[data-prayer-next-step]").textContent = result.nextStep || "";
        output.hidden = false;
        output.querySelector("[data-prayer-title]").setAttribute("tabindex", "-1");
        output.querySelector("[data-prayer-title]").focus({ preventScroll: false });
      } catch (error) {
        const text = friendlyError(error, "The prayer could not be created right now.");
        if (text && myGeneration === generation) showMessage(message, text, true);
      } finally {
        if (myGeneration === generation) {
          submit.disabled = false;
          submit.textContent = submitLabel;
        }
      }
    });

    on(speed, "change", () => {
      if (audio) audio.playbackRate = currentSpeed();
    });

    on(audio, "play", () => {
      if (playButton) {
        playButton.textContent = "Pause";
        playButton.setAttribute("aria-pressed", "true");
      }
    });
    on(audio, "pause", () => {
      if (playButton && audio.src) {
        playButton.textContent = audio.ended ? "Listen again" : "Resume";
        playButton.setAttribute("aria-pressed", "false");
      }
    });

    on(playButton, "click", async () => {
      if (!prayer) return;
      if (audioBlob) {
        if (audio.paused) await play();
        else audio.pause();
        return;
      }
      const myGeneration = generation;
      playButton.disabled = true;
      playButton.textContent = "Preparing audio...";
      try {
        await ensureAudio();
        if (myGeneration !== generation) return;
        showMessage(audioMessage, "Audio is ready. Use the controls to pause, seek, or adjust volume.");
        await play();
      } catch (error) {
        const text = friendlyError(error, "Audio could not be prepared right now.");
        if (text && myGeneration === generation) showMessage(audioMessage, text, true);
        if (myGeneration === generation) playButton.textContent = "Listen";
      } finally {
        if (myGeneration === generation) playButton.disabled = false;
      }
    });

    on(replayButton, "click", async () => {
      if (!audioBlob) {
        playButton?.click();
        return;
      }
      audio.currentTime = 0;
      await play();
    });

    on(downloadButton, "click", async () => {
      if (!prayer) return;
      const myGeneration = generation;
      downloadButton.disabled = true;
      try {
        const blob = await ensureAudio();
        if (myGeneration !== generation) return;
        saveBlob(blob, "faith-through-this-prayer.mp3");
      } catch (error) {
        const text = friendlyError(error, "Audio could not be downloaded right now.");
        if (text && myGeneration === generation) showMessage(audioMessage, text, true);
      } finally {
        if (myGeneration === generation) downloadButton.disabled = false;
      }
    });

    on(textButton, "click", () => {
      if (!prayer) return;
      saveBlob(new Blob([prayerAsText()], { type: "text/plain;charset=utf-8" }), "faith-through-this-prayer.txt");
      showMessage(audioMessage, "Prayer text downloaded to your device.");
    });

    on(copyButton, "click", async () => {
      if (!prayer) return;
      try {
        await navigator.clipboard.writeText(prayerAsText());
        showMessage(audioMessage, "Prayer copied.");
      } catch {
        showMessage(audioMessage, "Copy is not available in this browser. You can download the text instead.", true);
      }
    });

    return {
      destroy() {
        generation += 1;
        prayerController?.abort();
        resetAudio();
        cleanups.forEach((fn) => fn());
      },
    };
  };

  const registry = [
    ["[data-menu-toggle]", initMenu],
    ["[data-prayer-library]", initLibrary],
    ["[data-prayer-generator]", initGenerator],
  ];

  const init = (root = document) => {
    registry.forEach(([selector, factory]) => {
      const nodes = [];
      if (root instanceof Element && root.matches(selector)) nodes.push(root);
      root.querySelectorAll(selector).forEach((node) => nodes.push(node));
      nodes.forEach((node) => {
        if (controllers.has(node)) return;
        controllers.set(node, factory(node));
      });
    });
  };

  const destroy = (root) => {
    registry.forEach(([selector]) => {
      const nodes = [];
      if (root.matches?.(selector)) nodes.push(root);
      root.querySelectorAll(selector).forEach((node) => nodes.push(node));
      nodes.forEach((node) => {
        controllers.get(node)?.destroy();
        controllers.delete(node);
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));

  window.addEventListener("pagehide", () => destroy(document));
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) init(document);
  });
})();
